import React, { useState, useEffect } from "react";
import "./Headerup.css";
import MegaMenu from "./MegaMenu";
import { Link, useNavigate, useLocation } from "react-router-dom";
import categoryService from "../../services/categoryService";
import productService from "../../services/productService";
import { useAuth } from "../../context/AuthContext";

const HeaderUp = ({ cartCount = 0, onProductClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [menuData, setMenuData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setExpandedMenu(null);
  }, [location.pathname]);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryService.getCategoryTree();

      if (response?.success && response?.data) {
        const formattedMenuData = await formatCategoryTree(response.data);
        console.log(formattedMenuData, "formattedMenuData in header");
        setMenuData(formattedMenuData);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      // Fallback to static data if API fails
      setMenuData(getDefaultMenuData());
    } finally {
      setLoading(false);
    }
  };

  // Fetch featured products for a specific category
  const fetchFeaturedProducts = async (categoryId) => {
    try {
      const response = await productService.getProductsByFeaturedCategory(
        categoryId,
        { limit: 4 }
      );

      if (response?.success && response?.data) {
        // Format products for display
        console.log(response, "response hehehhe");
        return response.data.map((product) => ({
          id: product._id,
          _id: product._id,
          name: product.name,
          price: product.price,
          offerPrice: product.offerPrice,
          image: product.images || "https://via.placeholder.com/300x400",
          images: product.images || [],
          slug: product.sku || product._id,
          sizes: product.sizes || [],
          stock: product.stock || 0,
          description: product.description || "",
          category: product.category,
          isNew: product.isNew || false,
          isFeatured: product.isFeatured || false,
          isHotDeal: product.isHotDeal || false,
          ratings: product.ratings,
        }));
      }
      return [];
    } catch (error) {
      console.error("Error fetching featured products:", error);
      return [];
    }
  };

  // Format API response into menu structure
  const formatCategoryTree = async (categories) => {
    const menuStructure = {};

    // Filter only active root categories
    const rootCategories = categories.filter((cat) => cat.isActive);

    // Process each root category sequentially
    for (const rootCat of rootCategories) {
      // Create slug-friendly key from root category name
      const key =
        rootCat.slug || rootCat.name.toLowerCase().replace(/\s+/g, "");

      menuStructure[key] = {
        id: rootCat._id,
        name: rootCat.name,
        slug: rootCat.slug,
        categories: [],
        products: [], // Will be populated with featured products
      };

      // Fetch featured products for this root category
      const featuredProducts = await fetchFeaturedProducts(rootCat._id);
      menuStructure[key].products = featuredProducts;

      // Get second level children (like "Clothing", "Shop By Age")
      if (rootCat.children && rootCat.children.length > 0) {
        rootCat.children.forEach((secondLevel) => {
          const categorySection = {
            id: secondLevel._id,
            title: secondLevel.name,
            slug: secondLevel.slug,
            items: [],
          };

          // Get third level children (like "Dresses", "Tops")
          if (secondLevel.children && secondLevel.children.length > 0) {
            categorySection.items = secondLevel.children.map((thirdLevel) => ({
              id: thirdLevel._id,
              name: thirdLevel.name,
              slug: thirdLevel.slug,
            }));
          }

          menuStructure[key].categories.push(categorySection);
        });
      }
    }

    return menuStructure;
  };

  // Fallback static data
  const getDefaultMenuData = () => ({
    newborn: {
      name: "New Born",
      slug: "newborn",
      categories: [
        {
          title: "Clothing",
          items: [
            { name: "Dresses", slug: "dresses" },
            { name: "Tops & T-Shirts", slug: "tops-t-shirts" },
            { name: "Pants & Leggings", slug: "pants-leggings" },
            { name: "Skirts", slug: "skirts" },
            { name: "Jackets & Coats", slug: "jackets-coats" },
          ],
        },
      ],
      products: [],
    },
    girls: {
      name: "Girls",
      slug: "girls",
      categories: [
        {
          title: "Clothing",
          items: [
            { name: "Dresses", slug: "dresses" },
            { name: "Tops & T-Shirts", slug: "tops-t-shirts" },
            { name: "Pants & Leggings", slug: "pants-leggings" },
            { name: "Skirts", slug: "skirts" },
            { name: "Jackets & Coats", slug: "jackets-coats" },
          ],
        },
        {
          title: "Shop By Age",
          items: [
            { name: "Hair Accessories", slug: "hair-accessories" },
            { name: "Bags", slug: "bags" },
            { name: "Jewelry", slug: "jewelry" },
          ],
        },
      ],
      products: [],
    },
    boys: {
      name: "Boys",
      slug: "boys",
      categories: [
        {
          title: "Clothing",
          items: [
            { name: "T-Shirts & Polos", slug: "t-shirts-polos" },
            { name: "Shirts", slug: "shirts" },
            { name: "Pants & Jeans", slug: "pants-jeans" },
            { name: "Shorts", slug: "shorts" },
            { name: "Jackets & Hoodies", slug: "jackets-hoodies" },
          ],
        },
        {
          title: "Shop By Age",
          items: [
            { name: "Caps & Hats", slug: "caps-hats" },
            { name: "Backpacks", slug: "backpacks" },
            { name: "Belts", slug: "belts" },
          ],
        },
      ],
      products: [],
    },
    gifts: {
      name: "Gift Hamper",
      slug: "gifts",
      categories: [
        {
          title: "Gift Sets",
          items: [
            { name: "Birthday Hampers", slug: "birthday-hampers" },
            { name: "Newborn Gift Sets", slug: "newborn-gift-sets" },
            { name: "Holiday Bundles", slug: "holiday-bundles" },
            { name: "Personalized Gifts", slug: "personalized-gifts" },
          ],
        },
        {
          title: "Occasions",
          items: [
            { name: "Birthday", slug: "birthday" },
            { name: "Baby Shower", slug: "baby-shower" },
            { name: "Christening", slug: "christening" },
          ],
        },
      ],
      products: [],
    },
  });

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    setExpandedMenu(null);

    // Prevent body scroll when menu is open
    if (newState) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  };

  const toggleSubmenu = (menuName) => {
    setExpandedMenu(expandedMenu === menuName ? null : menuName);
  };

  const handleMenuClick = () => {
    setTimeout(() => {
      setIsMobileMenuOpen(false);
      setExpandedMenu(null);
      document.body.style.overflow = "unset";
    }, 300);
  };

  // Search products as user types
  useEffect(() => {
    const delaySearch = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setSearchLoading(true);
        try {
          const response = await productService.searchProducts(searchQuery);
          if (response?.success && response?.data) {
            setSearchResults(response.data.slice(0, 6)); // Show max 6 results
            setShowSearchResults(true);
          }
        } catch (error) {
          console.error("Search error:", error);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery("");
      setShowSearchResults(false);
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleProductClick = (product) => {
    navigate(`/products?search=${encodeURIComponent(product.name)}`);
    setSearchQuery("");
    setShowSearchResults(false);
  };

  const handleViewAllResults = () => {
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery("");
      setShowSearchResults(false);
    }
  };

  // Get menu keys dynamically
  const menuKeys = Object.keys(menuData);
  return (
    <>
      <header className="header">
        {/* Logo, Search, Icons Row */}
        <div className="header-middle">
          <div className="container">
            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-btn"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span className={`hamburger ${isMobileMenuOpen ? "open" : ""}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>

            <div className="logo">
              <Link to="/" className="logo-link">
                <img
                  src="/images/final_logo.png"
                  alt="Kitty-n-Mice"
                  className="logo"
                />
              </Link>
            </div>

            <div className="search-box">
              <form onSubmit={handleSearch}>
                <i className="fas fa-search" onClick={handleSearch}></i>
                <input
                  type="text"
                  placeholder="Search for products..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onFocus={() =>
                    searchQuery.trim().length >= 2 && setShowSearchResults(true)
                  }
                />
              </form>

              {/* Search Results Dropdown */}
              {showSearchResults && (
                <>
                  <div
                    className="search-overlay"
                    onClick={() => setShowSearchResults(false)}
                  />
                  <div className="search-results-dropdown">
                    {searchLoading ? (
                      <div className="search-loading">Searching...</div>
                    ) : searchResults.length > 0 ? (
                      <>
                        {searchResults.map((product) => (
                          <div
                            key={product._id}
                            className="search-result-item"
                            onClick={() => handleProductClick(product)}
                          >
                            {console.log(product, "product on search")}
                            <img
                              src={
                                (Array.isArray(product.images) &&
                                product.images.length > 0
                                  ? product.images[0]?.url
                                  : product.image) || "/images/placeholder.jpg"
                              }
                              alt={product.name}
                              className="search-result-image"
                            />
                            <div className="search-result-info">
                              <h4>{product.name}</h4>
                              <p className="search-result-price">
                                {product.offerPrice ? (
                                  <>
                                    <span className="offer-price">
                                      ₹{product.offerPrice}
                                    </span>
                                    <span className="original-price">
                                      ₹{product.price}
                                    </span>
                                  </>
                                ) : (
                                  <span className="offer-price">
                                    ₹{product.price}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div
                          className="search-view-all"
                          onClick={handleViewAllResults}
                        >
                          View all results for "{searchQuery}"
                        </div>
                      </>
                    ) : (
                      <div className="search-no-results">
                        No products found for "{searchQuery}"
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="header-icons">
              {/* <button className="icon-btn">
                <i className="far fa-heart"></i>
              </button> */}
              <Link to="/cart" className="icon-btn cart-btn">
                <i className="fas fa-shopping-bag"></i>
                <span className="cart-count">{cartCount}</span>
              </Link>
              {console.log(user, "user")}
              {user ? (
                <Link to="/myaccount" className="icon-btn" title="My Account">
                  <i className="far fa-user"></i>
                </Link>
              ) : (
                <Link
                  to="/signup"
                  className="icon-btn login-link"
                  title="Login"
                >
                  <span className="login-text">Sign up</span>
                  <i className="far fa-user"></i>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Navigation with Mega Menu */}
        <nav className="main-nav desktop-nav">
          <div className="container">
            {loading ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Loading...
              </div>
            ) : (
              <ul className="nav-list">
                {menuKeys.map((key) => (
                  <li key={key} className="nav-item has-mega-menu">
                    <Link
                      to={`/products/${menuData[key].name}`}
                      state={{
                        product: menuData[key]?.categories[0],
                        parent: true,
                      }}
                    >
                      {menuData[key].name ||
                        key.charAt(0).toUpperCase() + key.slice(1)}
                    </Link>
                    <MegaMenu
                      type={key}
                      menuData={menuData}
                      id={menuData[key]._id}
                      onProductClick={onProductClick}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </nav>
      </header>

      {/* Mobile Overlay */}
      <div
        className={`mobile-overlay ${isMobileMenuOpen ? "active" : ""}`}
        onClick={toggleMobileMenu}
      />

      {/* Mobile Sidebar */}
      <div className={`mobile-sidebar ${isMobileMenuOpen ? "open" : ""}`}>
        <div className="mobile-sidebar-header">
          <h2>Menu</h2>
          <button className="close-btn" onClick={toggleMobileMenu}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="mobile-menu-content">
          {loading ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              Loading menu...
            </div>
          ) : (
            menuKeys.map((key) => (
              <div key={key} className="mobile-menu-item">
                <button
                  className={`mobile-menu-header ${
                    expandedMenu === key ? "active" : ""
                  }`}
                  onClick={() => toggleSubmenu(key)}
                >
                  <span>
                    {menuData[key].name ||
                      key.charAt(0).toUpperCase() + key.slice(1)}
                  </span>
                  <svg
                    className={`arrow ${expandedMenu === key ? "rotated" : ""}`}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M4.427 7.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 7H4.604a.25.25 0 00-.177.427z" />
                  </svg>
                </button>
                <div
                  className={`mobile-submenu ${
                    expandedMenu === key ? "expanded" : ""
                  }`}
                >
                  {/* View All Link */}
                  {/* <div className="submenu-section view-all-section">
                    <Link
                      to={`/products/${menuData[key].name}`}
                      state={{
                        product: menuData[key]?.categories[0],
                        parent: true,
                      }}
                      onClick={handleMenuClick}
                      className="view-all-link"
                    >
                      View All {menuData[key].name}
                    </Link>
                  </div> */}

                  {menuData[key].categories &&
                  menuData[key].categories.length > 0
                    ? menuData[key].categories.map((category, index) => (
                        <div key={index} className="submenu-section">
                          <h4>{category.title}</h4>
                          {category.items && category.items.length > 0
                            ? category.items.map((item, idx) => (
                                <Link
                                  key={idx}
                                  to={`/products/${item.slug || item.name}`}
                                  state={{
                                    product: { id: item.id },
                                    parent: false,
                                  }}
                                  onClick={handleMenuClick}
                                >
                                  {item.name || item}
                                </Link>
                              ))
                            : null}
                        </div>
                      ))
                    : null}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default HeaderUp;
